import {faPlus} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {filter, find, findIndex} from 'lodash';
import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import Button from 'components/Button';
import KyError from 'components/Errors/KyError';
import Loading from 'components/Loading';
import ReadableWidthTemplate from 'components/Templates/ReadableWidth';
import useModalService from 'components/shared/ModalService';
import useAppContext from 'conf/AppContext';
import TechStackForm from 'pages/admin/techStack/Form';
import TechStackList from 'pages/admin/techStack/List';


export default function TechStackPage() {
  const appContext = useAppContext();
  const modalService = useModalService();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    const loadData = async () => {
      try {
        const rez = await appContext.api.get('api/v1/techStacks/list', {
          parseJson: true,
        }, abortController);
        setData(rez?.data.techStacks);
      } catch (err) {
        setError(err);
      }
    };
    // noinspection JSIgnoredPromiseFromCall
    loadData();

    return () => {
      abortController.abort();
    };

  }, []);

  const closeHandler = () => {
    modalService.close();
  };

  const saveHandler = (val) => {
    const techStackId = val?.data?.techStack?.techStackId;
    if (techStackId) {
      const existingIdx = findIndex(data, (ts) => ts.techStackId === techStackId);
      if (existingIdx >= 0) {
        const newTechStack = [...data];
        newTechStack.splice(existingIdx, 1, val?.data?.techStack);
        setData(newTechStack);
      } else {
        setData([val?.data?.techStack, ...data]);
      }
      toast.success(val?.message);
    }
    modalService.close();
  };

  const deleteSuccessHandler = (id) => {
    toast.success('Tech stack deleted.');
    setData(filter(data, (d) => d.techStackId !== id));
  };

  const deleteFailureHandler = (id, err) => {
    appContext.toastError(<KyError kyError={err} />);
  };
  
  
  const openEditEditor = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    let id;
    let techStack;
    const closestAncestor = evt.target.closest('button[data-id]');
    if (closestAncestor && closestAncestor.getAttribute('data-id')) {
      id = closestAncestor.getAttribute('data-id');
      if (id) {
        techStack = find(data, (ts) => ts.techStackId === id);
        if (!techStack) {
          appContext.toastError(`Cannot find Tech Stack ID ${id}!  Try reloading the page.`);
          return;
        }
      }
    }

    if (error) {
      setError(false);
    }

    modalService.open({
      closeHandler,
      style: {
        width: '40em',
      },
      content: (
          
        <TechStackForm
          techStack={techStack}
          onSave={saveHandler}
          onClose={closeHandler}
        />
      ),
    });
  };

  let body = <Loading />;
  if (error) {
    body = <KyError kyError={error} />;
  } else if (data) {
    body = (
      <TechStackList
        techStacks={data}
        onEdit={openEditEditor}
        onSuccess={deleteSuccessHandler}
        onFailure={deleteFailureHandler}
      />
    );
  }


  const openNewEditor = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    let techStack;
    const closestAncestor = evt.target.closest('button[data-id]');
    if (closestAncestor && closestAncestor.getAttribute('data-id')) {
      techStack = closestAncestor.getAttribute('data-id');
      if (!techStack) {
        appContext.toastError(`Cannot find tech stack ${techStack}! Try reloading the page.`);
        return;
      }
    } else {
      appContext.toastError('Cannot find data-id! Try reloading the page.');
      return;
    }

    if (error) {
      setError(false);
    }

    modalService.open({
      closeHandler,
      style: {
        width: '40em',
        overflow: 'unset',
      },
      content: (
        <TechStackForm
          onSave={saveHandler}
          onClose={closeHandler}
        />
      ),
    });
  };

  const renderTechStackBtn = () => (
    <div className="mb-2">
      <Button actionHandler={openNewEditor} data-id="tech-stack">
        <FontAwesomeIcon icon={faPlus} />Add tech stack
      </Button>
    </div>
  );
      
  return (
    <ReadableWidthTemplate title="Tech Stack Page">
      {renderTechStackBtn()}
      {body}
    </ReadableWidthTemplate>
  );
}
